import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, AlertCircle, Loader2, MessageCircle, Mail } from "lucide-react";
import { whatsappHref, emailHref } from "@/components/WhatsAppButton";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [data, setData] = useState(null);
  const [booking, setBooking] = useState(null);
  const attemptsRef = useRef(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          // Try to fetch booking details for richer contact messages
          const bookingId = data.metadata?.booking_id;
          if (bookingId) {
            try {
              const { data: b } = await api.get(`/bookings/${bookingId}`);
              setBooking(b);
            } catch (err) {
              console.warn("Could not load booking details:", err?.response?.status || err?.message);
            }
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (err) {
        // Transient errors during polling — continue to next attempt
        console.warn("Payment status poll attempt failed:", err?.response?.status || err?.message);
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= 8) {
        setStatus("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24">
      <Card className="p-10 text-center border-border" data-testid="booking-success-card">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin text-primary" size={36} />
            <h1 className="font-display text-3xl mt-6">Confirming your payment…</h1>
            <p className="text-muted-foreground mt-3">Please don't close this window.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <span className="inline-flex h-14 w-14 rounded-full bg-primary text-primary-foreground items-center justify-center mx-auto">
              <Check size={28} />
            </span>
            <h1 className="font-display text-3xl mt-6">Booking confirmed</h1>
            <p className="text-muted-foreground mt-3">You'll receive a confirmation email shortly.</p>
            {data && (
              <p className="font-display text-2xl mt-6">£{(data.amount_total / 100).toFixed(2)} {String(data.currency).toUpperCase()}</p>
            )}
            <div className="mt-8 flex gap-3 justify-center flex-wrap">
              <Button asChild data-testid="success-dashboard-btn"><Link to="/dashboard">View bookings</Link></Button>
              <Button asChild variant="outline"><Link to="/">Back home</Link></Button>
            </div>
            <div className="mt-6 pt-6 border-t border-border flex gap-3 justify-center flex-wrap">
              <a
                href={whatsappHref(
                  booking
                    ? `Hi, I just paid for booking #${booking.id.slice(0, 8)} (${booking.pickup_location} → ${booking.dropoff_location}, ${booking.pickup_date}, £${booking.total_price}${booking.crosses_channel ? " + Eurotunnel" : ""}). Pickup details please?`
                    : `Hi, I just paid for booking session ${sessionId?.slice(-12) || ""}. Can you confirm pickup details?`,
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-[#25D366] text-white font-medium text-sm hover:bg-[#1ebe57] transition-colors"
                data-testid="success-whatsapp-btn"
              >
                <MessageCircle size={16} fill="white" stroke="#25D366" /> WhatsApp us
              </a>
              <a
                href={emailHref(
                  booking
                    ? `Booking #${booking.id.slice(0, 8)} — ${booking.pickup_location} → ${booking.dropoff_location}`
                    : `Booking confirmation — session ${sessionId?.slice(-12) || ""}`,
                  booking
                    ? `Hi,\n\nI just paid for booking #${booking.id.slice(0, 8)}.\n\nRoute: ${booking.pickup_location} → ${booking.dropoff_location}\nPickup date: ${booking.pickup_date}\nVehicle: ${booking.vehicle}\nPet: ${booking.pet_name} (${booking.pet_type})\nTotal paid: £${booking.total_price}${booking.crosses_channel ? "\nNote: route crosses the Channel — Eurotunnel fee paid separately" : ""}\n\nPlease confirm pickup details.\n\nThanks.`
                    : `Hi,\n\nI just completed payment for booking session ${sessionId || ""}${data ? ` (£${(data.amount_total / 100).toFixed(2)} ${String(data.currency).toUpperCase()})` : ""}.\n\nPlease confirm pickup details.\n\nThanks.`,
                )}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-foreground text-background font-medium text-sm hover:bg-primary transition-colors"
                data-testid="success-email-btn"
              >
                <Mail size={16} /> Email us
              </a>
            </div>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <AlertCircle className="mx-auto text-destructive" size={36} />
            <h1 className="font-display text-3xl mt-6">Payment {status === "expired" ? "expired" : "could not be confirmed"}</h1>
            <p className="text-muted-foreground mt-3">Please try again or check your dashboard.</p>
            <div className="mt-8 flex gap-3 justify-center">
              <Button onClick={() => nav("/dashboard")}>Open dashboard</Button>
              <Button asChild variant="outline"><Link to="/book">Try again</Link></Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
